/**
 * Hook to track online/offline status with sync capabilities
 */
import { useState, useEffect, useCallback, useRef } from 'react';

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const onReconnectCallbacks = useRef([]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        // Trigger reconnect callbacks
        onReconnectCallbacks.current.forEach(cb => cb());
      }
      // Clear "was offline" after a brief delay so UI can show "Back online" message
      setTimeout(() => setWasOffline(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  const onReconnect = useCallback((callback) => {
    onReconnectCallbacks.current.push(callback);
    return () => {
      onReconnectCallbacks.current = onReconnectCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  return { isOnline, wasOffline, onReconnect };
}
